// SPDX-License-Identifier: Apache-2.0

// satellites.ts — rose-tinted Claude-session satellites orbiting the brain
// core. Port of v1's PeerSatellite (vault-orbit.tsx ~L1109).
//
// One satellite per live session. Each:
//   - orbits the brain core at a fixed radius (3.5 world units, just outside
//     the doc-node ring; v1 used the same radius)
//   - is a small SphereGeometry + MeshPhysicalMaterial rose-tinted (#ff7a94)
//   - is connected to the brain core via a thin LineSegments2 "tendril"
//   - pulses (scale + emissive bump → 1.5 emissive intensity, 1.4× scale)
//     on tool_use events touching that session, fading over 800ms
//
// Labels: piggyback on the existing DocLabelLayer's world-to-screen projection
// in doc-label.tsx via a sibling helper component (see satellite-label.tsx).
// Splitting label rendering vs satellite-mesh rendering keeps the engine
// imperative loop free of DOM churn — the loop just exposes a positions
// accessor that the Solid label layer reads via createMemo.
//
// API SURFACE matches the rest of orbit/: a constructor that returns a small
// imperative handle. The Solid wrapper drives sync/pulse via createEffect.

import * as THREE from "three";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

export interface SatelliteSession {
  id: string;
  title: string;
  /** ms epoch — newest activity. Used to decide initial pulse on resync. */
  lastTouched: number;
}

export interface SatelliteSnapshot {
  id: string;
  title: string;
  /** Current world-space position. */
  pos: THREE.Vector3;
}

export interface SatelliteLayer {
  /** Group containing all satellite meshes + tendril lines. */
  group: THREE.Group;
  /** Replace the active session set. Adds/removes satellites in place. */
  syncSessions(sessions: SatelliteSession[]): void;
  /** Bump a single satellite's pulse. Fades over PULSE_DURATION_MS. */
  pulse(sessionId: string): void;
  /** Per-frame update — called from the engine render loop. */
  update(deltaSec: number, elapsedSec: number): void;
  /** Snapshot for label rendering — DOM layer reads this every frame. */
  snapshot(): SatelliteSnapshot[];
  dispose(): void;
}

const ORBIT_RADIUS = 3.5;
const ORBIT_PERIOD_SEC = 14;     // full revolution time (visible motion, not vertigo)
const PULSE_DURATION_MS = 800;
const ROSE_HEX = 0xff7a94;

interface SatelliteEntry {
  id: string;
  title: string;
  /** Phase offset around the orbit (radians). */
  phase: number;
  /** Vertical tilt (small, so satellites are not coplanar). */
  yTilt: number;
  /** ms epoch of most recent pulse — 0 = no pulse yet. */
  pulseAt: number;
  mesh: THREE.Mesh;
  tendril: LineSegments2;
  tendrilGeom: LineSegmentsGeometry;
  baseEmissive: number;
  baseScale: number;
}

export function createSatellites(
  scene: THREE.Object3D,
  brainCorePos: THREE.Vector3,
): SatelliteLayer {
  const group = new THREE.Group();
  group.name = "vault-orbit-satellites";
  scene.add(group);

  const sphereGeom = new THREE.SphereGeometry(0.16, 18, 18);
  const entries = new Map<string, SatelliteEntry>();

  // Reusable tendril material — every satellite gets its own LineSegments2 to
  // avoid sharing a mutable geometry, but the material is cheap enough we
  // could share it. We do share to cap GPU material count.
  const tendrilMat = new LineMaterial({
    color: ROSE_HEX,
    linewidth: 0.025,
    transparent: true,
    opacity: 0.55,
    worldUnits: true,
    vertexColors: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    dashed: false,
    resolution: new THREE.Vector2(1920, 1080),
  });

  // Phase distribution: golden-angle so a growing fleet stays visually evenly
  // spaced without collision. yTilt jittered ±0.6 rad.
  function pickPhase(idx: number): { phase: number; yTilt: number } {
    return {
      phase: (idx * 0.61803) * Math.PI * 2,
      yTilt: ((idx * 0.37) % 1.0 - 0.5) * 1.2,
    };
  }

  function makeEntry(session: SatelliteSession, slot: number): SatelliteEntry {
    const mat = new THREE.MeshPhysicalMaterial({
      color: ROSE_HEX,
      emissive: ROSE_HEX,
      emissiveIntensity: 0.55,
      roughness: 0.32,
      metalness: 0.18,
      clearcoat: 0.35,
      clearcoatRoughness: 0.22,
      transparent: true,
      opacity: 1.0,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(sphereGeom, mat);
    mesh.frustumCulled = false;

    // Tendril: 2-vertex LineSegments2 (one segment from satellite → brain core)
    const tendrilGeom = new LineSegmentsGeometry();
    tendrilGeom.setPositions(new Float32Array(6)); // updated per frame
    const tendril = new LineSegments2(tendrilGeom, tendrilMat);
    tendril.frustumCulled = false;
    // NOTE: do NOT call tendril.computeLineDistances() — falls through to
    // Line.prototype which reads geometry.attributes.position.count, but
    // LineSegmentsGeometry uses instanceStart/instanceEnd interleaved
    // buffers, so position is undefined and it crashes. computeLineDistances
    // is only meaningful for dashed materials; we use dashed:false.
    tendril.raycast = () => undefined;

    group.add(mesh);
    group.add(tendril);

    const { phase, yTilt } = pickPhase(slot);
    return {
      id: session.id,
      title: session.title,
      phase,
      yTilt,
      pulseAt: 0,
      mesh,
      tendril,
      tendrilGeom,
      baseEmissive: 0.55,
      baseScale: 1.0,
    };
  }

  function disposeEntry(e: SatelliteEntry) {
    group.remove(e.mesh);
    group.remove(e.tendril);
    (e.mesh.material as THREE.Material).dispose();
    e.tendrilGeom.dispose();
  }

  function syncSessions(sessions: SatelliteSession[]) {
    const seen = new Set<string>();
    sessions.forEach((s, idx) => {
      seen.add(s.id);
      let entry = entries.get(s.id);
      if (!entry) {
        entry = makeEntry(s, entries.size);
        entries.set(s.id, entry);
      } else {
        // Title can drift; keep it fresh.
        entry.title = s.title;
      }
      // Re-slot phase if the index is way off (large fleet rebalancing).
      const { phase, yTilt } = pickPhase(idx);
      entry.phase = phase;
      entry.yTilt = yTilt;
    });
    // Drop satellites whose session went away.
    for (const [id, entry] of entries) {
      if (!seen.has(id)) {
        disposeEntry(entry);
        entries.delete(id);
      }
    }
  }

  function pulse(sessionId: string) {
    const entry = entries.get(sessionId);
    if (!entry) return;
    entry.pulseAt = performance.now();
  }

  // Per-frame: orbit the satellite, update the tendril endpoints, fade pulse.
  const tmpVec = new THREE.Vector3();
  function update(_deltaSec: number, elapsedSec: number) {
    const now = performance.now();
    for (const entry of entries.values()) {
      // Orbit position — circular in the xz-plane with a small y-tilt.
      const angle = entry.phase + (elapsedSec / ORBIT_PERIOD_SEC) * Math.PI * 2;
      const x = brainCorePos.x + Math.cos(angle) * ORBIT_RADIUS;
      const z = brainCorePos.z + Math.sin(angle) * ORBIT_RADIUS;
      const y = brainCorePos.y + entry.yTilt + Math.sin(elapsedSec * 0.6 + entry.phase) * 0.18;
      entry.mesh.position.set(x, y, z);

      // Pulse decay — exponential ramp over PULSE_DURATION_MS.
      const since = now - entry.pulseAt;
      let pulseAmt = 0;
      if (entry.pulseAt > 0 && since < PULSE_DURATION_MS) {
        pulseAmt = 1 - since / PULSE_DURATION_MS;
        // Ease-out cubic for a softer fade
        pulseAmt = pulseAmt * pulseAmt * pulseAmt;
      }
      const scale = entry.baseScale + pulseAmt * 0.45;
      entry.mesh.scale.setScalar(scale);
      const mat = entry.mesh.material as THREE.MeshPhysicalMaterial;
      mat.emissiveIntensity = entry.baseEmissive + pulseAmt * 1.5;

      // Tendril: satellite (start) → brain core (end). Brighten on pulse.
      const buf = new Float32Array(6);
      buf[0] = x;
      buf[1] = y;
      buf[2] = z;
      buf[3] = brainCorePos.x;
      buf[4] = brainCorePos.y;
      buf[5] = brainCorePos.z;
      entry.tendrilGeom.setPositions(buf);
    }

    // Tendril material opacity scales with the brightest pulse in flight, so
    // any pulse brings every tendril up briefly. Cheap shared knob.
    let maxPulse = 0;
    for (const e of entries.values()) {
      const since = now - e.pulseAt;
      if (e.pulseAt > 0 && since < PULSE_DURATION_MS) {
        const p = 1 - since / PULSE_DURATION_MS;
        if (p > maxPulse) maxPulse = p;
      }
    }
    tendrilMat.opacity = 0.55 + maxPulse * 0.35;
    void tmpVec; // reserved for future use
  }

  function snapshot(): SatelliteSnapshot[] {
    const out: SatelliteSnapshot[] = [];
    for (const entry of entries.values()) {
      out.push({
        id: entry.id,
        title: entry.title,
        pos: entry.mesh.position.clone(),
      });
    }
    return out;
  }

  function dispose() {
    for (const entry of entries.values()) disposeEntry(entry);
    entries.clear();
    sphereGeom.dispose();
    tendrilMat.dispose();
    if (group.parent) group.parent.remove(group);
  }

  return {
    group,
    syncSessions,
    pulse,
    update,
    snapshot,
    dispose,
  };
}
